/*
 * What the "Check this conversation" and "Draft again" routes say on the wire (`draft-again.ts`,
 * `draft-again-routes.ts`). Zod only, so the browser parses with the same definitions the server
 * answers from and imports nothing of the server with them.
 */
import { z } from "zod";

/** What one check can say. Each is one sentence on the screen and one test through the route. */
export const CHECK_FINDINGS = [
  /** Bison shows the reply answered; the conversation is settled now. */
  "answered",
  /** The lead wrote after this reply, so the newest message is the one to answer. */
  "lead_wrote_again",
  /** A new attempt is under way, or work of the last one is still running. */
  "being_drafted",
  /** A new attempt was asked for and ended without a result; what is shown is what was there. */
  "request_ended",
  /** The last attempt's execution is gone and the plan shows nothing for the reply. */
  "stopped",
  /** Nobody has answered in Bison and nothing is under way. */
  "unanswered",
  /** Bison or the workflow host could not be read; nothing is claimed either way. */
  "could_not_check",
] as const;
export type CheckFinding = (typeof CHECK_FINDINGS)[number];

/** What the open conversation may offer. */
export const DraftAgainOfferSchema = z.object({
  check: z.boolean(),
  draftAgain: z.boolean(),
  /** When a new attempt was asked for whose result is not shown yet; null once it is, or if none. */
  requestedSince: z.iso.datetime({ offset: true }).nullable(),
  /** That attempt is no longer on its way: it ended, and its result will not arrive. */
  requestEnded: z.boolean(),
  /**
   * A question is still shown as open and its run is closed, so an answer to it resumes nothing
   * (`closed-question.ts`). Optional: a server before this field omits it, and the browser bundle
   * before it reads the offer through this same non-strict object and ignores it.
   */
  questionClosed: z.boolean().optional(),
});
export type DraftAgainOffer = z.infer<typeof DraftAgainOfferSchema>;

/**
 * When the new attempt an offer says is still on its way was asked for, or null where none is: the
 * one reading the open conversation and its inbox row share (w563).
 */
export function awaitedSince(offer: DraftAgainOffer | null | undefined): string | null {
  return offer?.requestedSince && !offer.requestEnded ? offer.requestedSince : null;
}

/**
 * Why a request drafted nothing (409). With `questionClosed`, the refusal arrived after the
 * question's run was closed for the attempt, or the question was closed already: the page must not
 * say that nothing changed. Optional, as the offer's field is, and for the same two reasons.
 */
export const RefusalSchema = z.object({
  error: z.string(),
  questionClosed: z.boolean().optional(),
});
export type Refusal = z.infer<typeof RefusalSchema>;

export const CheckResponseSchema = z.object({
  finding: z.enum(CHECK_FINDINGS),
  draftAgain: z.boolean(),
});
export type CheckResponse = z.infer<typeof CheckResponseSchema>;
